import React, { useState, useEffect } from 'react';
import './App.css';
import { initialStudents } from './data/mockData';
import logoImg from './assets/logo.png';

// Auth & Dashboards
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Profile from './components/Profile';
import AdminDashboard from './components/AdminDashboard';
import StudentDashboard from './components/StudentDashboard';

// Admin Components
import PoolsList from './components/PoolsList';
import CreatePool from './components/CreatePool';
import ProgramsList from './components/ProgramsList';
import ViewBatches from './components/ViewBatches';
import CreateBatch from './components/CreateBatch';
import ViewStudents from './components/ViewStudents';
import EnrollStudent from './components/EnrollStudent';
import BatchDetails from './components/BatchDetails';
import AttendanceModal from './components/AttendanceModal';
import PaymentModal from './components/PaymentModal';
import StudentModal from './components/StudentModal';
import Accounts from './components/Accounts';

export default function App() {
  // --- AUTH STATE ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authView, setAuthView] = useState('login');
  const [userRole, setUserRole] = useState(null);
  const [userUid, setUserUid] = useState(null);
  // const [studentProfileId, setStudentProfileId] = useState(null); // Tracks actual MongoDB Student ID

  const [adminUser, setAdminUser] = useState(null);

  // --- DASHBOARD STATE ---
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedProgram, setSelectedProgram] = useState(null);

  const [pools, setPools] = useState([]);
  const [batches, setBatches] = useState([]);
  const [enrollments, setEnrollments] = useState([]); // Start empty for real API
  const [enrollmentsFull, setEnrollmentsFull] = useState([]); // Start empty for real API

  // UPDATED: Now dynamic state for API fetch instead of static mock
  const [students, setStudents] = useState([]);

  const [selectedPoolId, setSelectedPoolId] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, type: null, enrollmentId: null, studentId: null });

  // Fallback simulated user if no students are fetched yet for the StudentDashboard
  const simulatedStudentUser = students.length > 0 ? students[0] : initialStudents[0];

  // --- MOBILE MENU STATE ---
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // --- 1. AUTHENTICATION EFFECT ---
  useEffect(() => {
    const getUserRole = async () => {
      try {
        const res = await fetch('https://tsaapis.operate.live/api/auth/me', {
          method: 'GET',
          credentials: 'include'
        });

        if (res.status !== 200) {
          window.location.href = 'https://tsa.operate.live/login.html';
          return;
        }

        const data = await res.json();

        if (data.success) {
          const role = data.user.role;
          const userId = data.user.id;
          localStorage.setItem('token', data.user.tokem);
          setUserUid(data.user.uid);

          if (role === 'a') {
            setUserRole('admin');
            setIsAuthenticated(true);
            setActiveTab('dashboard');
          } else if (role === 's') {
            setUserRole('student');
            setIsAuthenticated(true);
            setActiveTab('dashboard');
          } else if (role === 'u') {
            // window.location.href = 'https://tsa.operate.live/profile.html?id=' + userId;
            alert("Your email verification is pending, please complete to proceed!");
            window.location.href = 'https://tsa.operate.live/login.html';
          }
        } else {
          window.location.href = 'https://tsa.operate.live/login.html';
        }
      } catch (err) {
        console.log(err);
        window.location.href = 'https://tsa.operate.live/login.html';
      }
    };

    getUserRole();
  }, []);

  // --- 2E. FETCH ADMIN PROFILE ---
  const fetchAdminProfile = async () => {
    if (!userUid) return;

    try {
      const res = await fetch(`https://tsaapis.operate.live/api/admin/${userUid}`, {
        method: 'GET',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        // Map database schema to frontend expectations
        setAdminUser({
          id: data._id,
          userId: data.userId,
          name: data.name,
          email: data.email,
          phone: data.mobile || data.phone,
          picUrl: data.picUrl,
          role: data.role
        });
      }
    } catch (err) {
      console.error("API Error fetching admin profile:", err);
    }
  };

  // --- 2A. FETCH POOLS ---
  const fetchPools = async () => {
    try {
      const res = await fetch('https://tsaapis.operate.live/api/pools', {
        method: 'GET',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        const cacheBuster = Date.now(); // <-- NEW: Generate timestamp

        const mappedPools = data.map(p => ({
          id: p._id,
          name: p.Name,
          title: p.Title,
          // NEW: Append timestamp to force image refresh across the whole app
          pic: p.picUrl ? `${p.picUrl}?t=${cacheBuster}` : '',
          address: p.Address,
          facilities: p.Facilities || [],
          mapUrl: p.mapsURL,
          programs: p.programs || [],
          capacity: Number(p.Capacity || p.capacity)|| 0,
          isActive: p.active !== false
        }));

        setPools(mappedPools); // This state update triggers ProgramsList to refresh
        return mappedPools;
      }
    } catch (err) {
      console.error("API Error fetching pools:", err);
    }
    return null;
  };

  // --- 2B. FETCH BATCHES ---
  const fetchBatches = async () => {
    try {
      const res = await fetch('https://tsaapis.operate.live/api/batches', {
        method: 'GET',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        const mappedBatches = data.map(b => ({
          id: b._id,
          poolId: b.poolId,
          title: b.Title,
          type: b.Type,
          fees: b.Fees,
          capacity: b.Capacity || 15,
          hoursCount: b.TotalHours,
          hoursPerDay: b.HoursPerDay,
          startTime: b.StartTime,
          endTime: b.EndTime,
          validDays: b.ValidDays,
          daysAllowed: b.daysAllowed || [],
          isActive: b.active !== false
        }));
        setBatches(mappedBatches);
        return mappedBatches;
      }
    } catch (err) {
      console.error("API Error fetching batches:", err);
    }
    return null;
  };

  // --- 2C. FETCH ENROLLMENTS ---
  const fetchEnrollments = async () => {
    try {
      const res = await fetch('https://tsaapis.operate.live/api/enrolments', {
        method: 'GET',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();

        // Map Database schema to the dashboard UI format
        const mappedEnrollments = data.map(e => ({
          id: e._id,
          studentId: e.studentId?._id || e.studentId, // Support populated queries
          batchId: e.batchId?._id || e.batchId,
          startDay: new Date(e.EnrolledDate).toLocaleDateString(),
          endDay: new Date(e.ExpiryDate).toLocaleDateString(),
          usedHours: e.UsedHours || 0,
          balanceHours: (e.TotalHours || 0) - (e.UsedHours || 0),
          active: e.active !== false
        }));

        setEnrollments(mappedEnrollments);
      }
    } catch (err) {
      console.error("API Error fetching enrollments:", err);
    }
  };

  // --- 2C. FETCH ENROLLMENTS FULL ---
  const fetchEnrollmentsFull = async () => {
    try {
      const res = await fetch('https://tsaapis.operate.live/api/enrolments/full', {
        method: 'GET',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();

        const mappedEnrollments = data.map(e => ({
          // IDs
          id: e._id,
          paymentId: e.paymentId,
          studentId: e.studentId,
          batchId: e.batchId,
          poolId: e.poolId,

          // Student
          name: e.name,
          picUrl: e.picUrl,
          DOB: e.DOB,
          email: e.email,
          mobile: e.mobile,
          whatsapp: e.whatsapp,
          userId: e.userId,
          emergency: e.emergency,
          address: e.address,
          refferedBy: e.refferedBy,
          regId: e.regId,

          // Batch
          title: e.Title,
          type: e.Type,
          fees: e.Fees,
          capacity: e.Capacity,
          daysAllowed: e.daysAllowed || [],
          hoursPerDay: e.HoursPerDay,
          startTime: e.StartTime,
          endTime: e.EndTime,
          validDays: e.ValidDays,

          // Enrollment
          enrolledDate: e.EnrolledDate,
          expiryDate: e.ExpiryDate,
          startDay: new Date(e.EnrolledDate).toLocaleDateString(),
          endDay: new Date(e.ExpiryDate).toLocaleDateString(),
          totalHours: e.TotalHours || 0,
          usedHours: e.UsedHours || 0,
          balanceHours: (e.TotalHours || 0) - (e.UsedHours || 0),
          totalFees: e.TotalFees || 0,
          paidFees: e.PaidFees || 0,
          active: e.active,

          // Payment
          paidAmount: e.PaidAmount || 0,
          paidDate: e.PaidDT,
          paymentMode: e.Mode,
          transactionRef: e.TransactionRef,
          Status: e.Status,
          remarks: e.Remarks,

          // Metadata
          createdAt: e.createdAt,
          updatedAt: e.updatedAt
        }));

        setEnrollmentsFull(mappedEnrollments);
      }
    } catch (err) {
      console.error('API Error fetching enrollments:', err);
    }
  };

  // --- 2D. NEW: FETCH STUDENTS ---
  const fetchStudents = async () => {
    try {
      const res = await fetch('https://tsaapis.operate.live/api/students' + (userRole === 'student' ? '/' + userUid : ''), {
        method: 'GET',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();

        // Map Database schema to match Frontend expectations
        const mappedStudents = (Array.isArray(data) ? data : [data]).map(s => ({
          id: s._id,
          userId: s.userId,
          regId: s.regId, // Added new unique human-readable registration ID
          name: s.name,
          profilePic: s.picUrl, // Mapped picUrl to profilePic for UI compatibility
          dob: s.DOB ? new Date(s.DOB).toLocaleDateString() : 'N/A', // Formatting Date
          email: s.email,
          mobile: s.mobile,
          whatsapp: s.whatsapp,
          emergency: s.emergency,
          address: s.address,
          refferedBy: s.refferedBy
        }));

        setStudents(mappedStudents);
      }
    } catch (err) {
      console.error("API Error fetching students:", err);
    }
  };

  // --- 3. FETCH DATA ON LOGIN ---
  useEffect(() => {
    if (isAuthenticated) {
      fetchPools().then(mappedPools => {
        if (mappedPools && mappedPools.length > 0) {
          setSelectedPoolId(mappedPools[0].id);
          setSelectedPoolId(prev => prev || mappedPools[0].id);
        }
      });
      fetchBatches();
      fetchEnrollments();
      fetchEnrollmentsFull();
      fetchStudents(); // <-- Fetching students via DB API on login

      // if (userRole === 'student') {
      //   fetchStudentProfileId();
      // }
      // NEW: Fetch Admin Profile if user is an admin
      if (userRole === 'admin') {
        fetchAdminProfile();
      }
    }
  }, [isAuthenticated, userRole, userUid]);

  // --- HANDLERS ---
  const handleLogin = (role) => {
    setUserRole(role);
    setIsAuthenticated(true);
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    document.cookie = "tsa_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUserRole(null);
    setIsMobileMenuOpen(false);
    window.location.href = 'https://tsa.operate.live/login.html';
  };

  const changeTab = (tab, keepSubState = false) => {
    // console.log('keepSubState,selectedProgram',keepSubState,selectedProgram);
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    if (tab === 'programs' && !keepSubState) {
      setSelectedProgram(null);
    }
  };

  // 1. Add this calculation in the parent component where you have access to pools, batches, and enrollments
  const poolUtilisationMap = {};

  pools.forEach(pool => {
    const poolId = pool.id || pool._id;

    // Find all batches that belong to this pool
    const poolBatches = batches.filter(b => b.poolId === poolId).map(b => b.id || b._id);

    // Find all enrollments in those batches
    const poolEnrollments = enrollments.filter(e => poolBatches.includes(e.batchId));

    // Get unique student count for this pool
    const uniqueStudents = new Set(poolEnrollments.map(e => e.studentId)).size;

    poolUtilisationMap[poolId] = uniqueStudents;
  });

  if (!isAuthenticated) {
    return (
      <div className="auth-wrapper">
        {authView === 'register' && <Register setAuthView={setAuthView} handleLogin={handleLogin} />}
        {authView === 'forgot' && <ForgotPassword setAuthView={setAuthView} />}
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* DYNAMIC NAVBAR */}
      <nav>
        <div className="nav-brand">
          <img src={logoImg} alt="Logo" className="logo-img" />
          <div className="brand-text">The Swimmers <span>Academy</span></div>
        </div>

        <button className="hamburger-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>

        <div className={`admin-tabs ${isMobileMenuOpen ? 'mobile-open' : ''}`}>

          <button className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => changeTab('dashboard')}>
            <i className="fas fa-chart-line"></i> Dashboard
          </button>

          <button className={`tab-btn ${activeTab === 'programs' ? 'active' : ''}`} onClick={() => changeTab('programs')}>
            <i className="fas fa-water"></i> Pools
          </button>

          {userRole === 'admin' && (
            <>
              <button className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`} onClick={() => changeTab('students')}>
                <i className="fas fa-users"></i> Students
              </button>
            </>
          )}

          <button className={`tab-btn ${activeTab === 'accounts' ? 'active' : ''}`} onClick={() => changeTab('accounts')}>
            <i className="fas fa-file-invoice-dollar"></i> Accounts
          </button>

          <div className="nav-divider"></div>

          <button className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => changeTab('profile')}>
            <i className="fas fa-user-circle"></i> Profile
          </button>

          <button className="logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </nav>

      <div className="app-background">
        <div className="main-content">

          {/* DASHBOARD ROUTING */}
          {activeTab === 'dashboard' && userRole === 'admin' && (
            <AdminDashboard students={students} pools={pools} batches={batches} enrollments={enrollments} setActiveTab={changeTab} setModalConfig={setModalConfig} />
          )}

          {activeTab === 'dashboard' && userRole === 'student' && (
            <StudentDashboard studentUser={simulatedStudentUser} enrollments={enrollments} batches={batches} pools={pools} setActiveTab={changeTab} setEnrollments={setEnrollments} />
          )}

          {/* SHARED ROUTES */}
          {activeTab === 'programs' && !selectedProgram && (
            <div className="programs-container">
              <PoolsList pools={pools} selectedPoolId={selectedPoolId} setSelectedPoolId={setSelectedPoolId} setActiveTab={changeTab} userRole={userRole} poolUtilisationMap={poolUtilisationMap} />
              <ProgramsList setSelectedProgram={setSelectedProgram} selectedPool={pools.find(p => p.id === selectedPoolId)} fetchPools={fetchPools} setActiveTab={changeTab} userRole={userRole} />
            </div>
          )}

          {activeTab === 'programs' && selectedProgram && (
            <ViewBatches
              userUid={userUid}
              fetchEnrollments={fetchEnrollments}
              batches={batches.filter(p => p.poolId === selectedPoolId)}
              selectedProgram={selectedProgram}
              setSelectedProgram={setSelectedProgram}
              setActiveTab={changeTab}
              setSelectedBatchId={setSelectedBatchId}
              userRole={userRole}
            />
          )}

          {activeTab === 'profile' && (
            <Profile
              defaultUser={userRole === 'admin' ? (adminUser || {}) : (students[0] || {})}
              userRole={userRole}
              userUid={userUid}
            />
          )}

          {activeTab === 'accounts' && (
            <Accounts enrollments={userRole === 'admin' ? enrollmentsFull : enrollmentsFull.filter(s => s.studentId === userUid)} userRole={userRole} />
          )}

          {/* ADMIN ONLY ROUTES */}
          {userRole === 'admin' && (
            <>

              {(activeTab === 'create-pool' || activeTab === 'edit-pool') && (
                <CreatePool
                  fetchPools={fetchPools}
                  setActiveTab={changeTab}
                  setSelectedPoolId={setSelectedPoolId}
                  poolToEdit={activeTab === 'edit-pool' ? pools.find(p => p.id === selectedPoolId) : null}
                />
              )}

              {(activeTab === 'create' || activeTab === 'edit-batch') && selectedProgram && (
                <CreateBatch
                  setBatches={setBatches}
                  fetchBatches={fetchBatches}
                  setActiveTab={changeTab}
                  selectedProgram={selectedProgram}
                  selectedPoolId={selectedPoolId}
                  batchToEdit={activeTab === 'edit-batch' ? batches.find(b => b.id === selectedBatchId) : null}
                />
              )}

              {activeTab === 'students' && (
                <ViewStudents students={students} enrollments={enrollments} batches={batches} setActiveTab={changeTab} setSelectedBatchId={setSelectedBatchId} setModalConfig={setModalConfig} />
              )}

              {activeTab === 'enroll' && (
                <EnrollStudent students={students} batches={batches} setEnrollments={setEnrollments} setActiveTab={changeTab} selectedBatchId={selectedBatchId} />
              )}

              {activeTab === 'batch-details' && <BatchDetails batchId={selectedBatchId} batches={batches} enrollments={enrollments} students={students} setActiveTab={changeTab} setModalConfig={setModalConfig} />}
            </>
          )}

        </div>
      </div>

      {/* ADMIN MODALS */}
      {userRole === 'admin' && (
        <>
          {modalConfig.isOpen && modalConfig.type === 'attendance' && <AttendanceModal enrollmentId={modalConfig.enrollmentId} enrollments={enrollments} students={students} closeModal={() => setModalConfig({ isOpen: false })} />}
          {modalConfig.isOpen && modalConfig.type === 'payment' && <PaymentModal enrollmentId={modalConfig.enrollmentId} enrollments={enrollments} students={students} batches={batches} closeModal={() => setModalConfig({ isOpen: false })} />}
          {modalConfig.isOpen && modalConfig.type === 'student-info' && <StudentModal studentId={modalConfig.studentId} students={students} enrollments={enrollments} batches={batches} closeModal={() => setModalConfig({ isOpen: false })} />}
        </>
      )}
    </div>
  );
}